import {
  computeEqualSpacingSnapTargets,
  computeRhythmGapSnapTargets,
  isAlignmentNeighborWithinReach,
  resolveGapNeighborContext,
  serializeAlignmentBox,
  translateAlignmentBox,
  type AlignmentBox,
} from './alignmentMath';
import type { ActiveAlignmentGuide } from './types';

export const GUIDE_VISIBILITY_THRESHOLD_PX = 12;
export const MAGNETIC_ALIGNMENT_THRESHOLD_PX = 8;
export const MAGNETIC_EQUAL_SPACING_THRESHOLD_PX = 10;
/** Wider capture zone for vertical equal spacing — harder to feel than horizontal pairs. */
export const MAGNETIC_EQUAL_SPACING_Y_THRESHOLD_PX = 14;
export const MAGNETIC_RHYTHM_GAP_THRESHOLD_PX = 10;
export const MAX_MAGNET_DELTA_PX = 6;
export const MAGNETIC_EQUAL_SPACING_MAX_DELTA_PX = 10;
export const MAGNETIC_ALIGNMENT_MAX_DELTA_PX = 10;

export type MagneticSnapDisabledReason =
  | 'alt-key'
  | 'no-active-guide'
  | 'outside-threshold'
  | 'unsupported-mode';

export type MagneticSnapTargetType =
  | 'alignment'
  | 'equal-spacing-x'
  | 'equal-spacing-y'
  | 'equal-spacing-both'
  | 'matching-gap-x'
  | 'matching-gap-y'
  | 'matching-gap-both';

export type MagneticSnapDebug = {
  enabled: boolean;
  snapApplied: boolean;
  disabledReason?: MagneticSnapDisabledReason;
  rawPreviewBox: ReturnType<typeof serializeAlignmentBox>;
  magnetisedPreviewBox: ReturnType<typeof serializeAlignmentBox>;
  appliedDelta: { x: number; y: number };
  target?: {
    axis: 'x' | 'y' | 'both';
    type: MagneticSnapTargetType;
    targetBlockId?: string;
    movingAnchor?: string;
    targetAnchor?: string;
    distancePx: number;
    priority?: string;
    matchedGapPx?: number;
  };
  rhythmGaps?: number[];
  thresholdPx: number;
  equalSpacingThresholdPx?: number;
  rhythmGapThresholdPx?: number;
  altKey: boolean;
};

type AxisMagneticCandidate = {
  delta: number;
  distancePx: number;
  type: MagneticSnapTargetType;
  guide?: ActiveAlignmentGuide;
  priority?: string;
  matchedGapPx?: number;
};

function clampMagneticAxisDelta(
  snapDeltaPx: number,
  distancePx: number,
  thresholdPx: number = MAGNETIC_ALIGNMENT_THRESHOLD_PX,
  mode: 'alignment' | 'spacing' = 'alignment'
): number {
  if (!Number.isFinite(snapDeltaPx) || snapDeltaPx === 0) {
    return 0;
  }
  const sign = snapDeltaPx < 0 ? -1 : 1;
  const magnitude = Math.abs(snapDeltaPx);
  if (distancePx > thresholdPx) {
    return sign * Math.min(magnitude, MAX_MAGNET_DELTA_PX);
  }
  if (mode === 'spacing') {
    const spacingCap =
      distancePx <= 2
        ? magnitude
        : Math.min(
            magnitude,
            Math.max(
              MAX_MAGNET_DELTA_PX,
              Math.round(
                MAGNETIC_EQUAL_SPACING_MAX_DELTA_PX +
                  (thresholdPx - distancePx) * 0.65
              )
            )
          );
    return sign * spacingCap;
  }
  if (distancePx <= 2) {
    return sign * magnitude;
  }
  const boostedMax = Math.min(
    magnitude,
    Math.max(
      MAX_MAGNET_DELTA_PX,
      Math.round(MAX_MAGNET_DELTA_PX + (thresholdPx - distancePx) * 0.85)
    )
  );
  return sign * Math.min(magnitude, boostedMax, MAGNETIC_ALIGNMENT_MAX_DELTA_PX);
}

function resolveAxisMagneticDelta(
  guide: ActiveAlignmentGuide | undefined,
  thresholdPx: number
): { delta: number; distancePx: number } | null {
  if (!guide) return null;
  if (guide.distancePx > thresholdPx) return null;
  return {
    delta: clampMagneticAxisDelta(guide.snapDeltaPx, guide.distancePx, thresholdPx, 'alignment'),
    distancePx: guide.distancePx,
  };
}

function pickClosestAxisCandidate(candidates: AxisMagneticCandidate[]): AxisMagneticCandidate | null {
  if (candidates.length === 0) return null;
  return candidates.reduce((best, candidate) =>
    candidate.distancePx < best.distancePx ? candidate : best
  );
}

function spacingSnapPreferenceMarginPx(type: MagneticSnapTargetType): number {
  if (type === 'equal-spacing-y') return 5;
  if (type === 'equal-spacing-x') return 3;
  return 2;
}

function pickAxisMagneticCandidate(candidates: AxisMagneticCandidate[]): AxisMagneticCandidate | null {
  if (candidates.length === 0) return null;

  const spacingCandidates = candidates.filter((candidate) => isSpacingSnapType(candidate.type));
  const alignmentCandidates = candidates.filter((candidate) => candidate.type === 'alignment');

  if (spacingCandidates.length > 0) {
    const bestSpacing = pickClosestAxisCandidate(spacingCandidates);
    if (!bestSpacing) return pickClosestAxisCandidate(candidates);

    const bestAlignment =
      alignmentCandidates.length > 0 ? pickClosestAxisCandidate(alignmentCandidates) : null;
    if (!bestAlignment) return bestSpacing;

    const marginPx = spacingSnapPreferenceMarginPx(bestSpacing.type);
    if (bestSpacing.distancePx <= bestAlignment.distancePx + marginPx) {
      return bestSpacing;
    }
    return bestAlignment;
  }

  return pickClosestAxisCandidate(alignmentCandidates);
}

function isSpacingSnapType(type: MagneticSnapTargetType) {
  return type.startsWith('equal-spacing') || type.startsWith('matching-gap');
}

function resolveCombinedSnapType(
  xCandidate: AxisMagneticCandidate | null,
  yCandidate: AxisMagneticCandidate | null
): MagneticSnapTargetType {
  if (xCandidate && yCandidate) {
    if (xCandidate.type === 'alignment' && yCandidate.type === 'alignment') {
      return 'alignment';
    }
    if (xCandidate.type === 'equal-spacing-x' && yCandidate.type === 'equal-spacing-y') {
      return 'equal-spacing-both';
    }
    if (xCandidate.type === 'matching-gap-x' && yCandidate.type === 'matching-gap-y') {
      return 'matching-gap-both';
    }
    if (isSpacingSnapType(xCandidate.type) && isSpacingSnapType(yCandidate.type)) {
      return xCandidate.type.startsWith('matching-gap') || yCandidate.type.startsWith('matching-gap')
        ? 'matching-gap-both'
        : 'equal-spacing-both';
    }
    return 'alignment';
  }
  return xCandidate?.type ?? yCandidate!.type;
}

function buildMagneticTarget(
  xCandidate: AxisMagneticCandidate | null,
  yCandidate: AxisMagneticCandidate | null
): MagneticSnapDebug['target'] | undefined {
  if (!xCandidate && !yCandidate) return undefined;

  const axis: 'x' | 'y' | 'both' = xCandidate && yCandidate ? 'both' : xCandidate ? 'x' : 'y';
  const primaryGuide = xCandidate?.guide ?? yCandidate?.guide;
  const distancePx =
    axis === 'both'
      ? Math.min(xCandidate!.distancePx, yCandidate!.distancePx)
      : xCandidate?.distancePx ?? yCandidate!.distancePx;

  const matchedGapPx =
    axis === 'both'
      ? xCandidate?.matchedGapPx ?? yCandidate?.matchedGapPx
      : xCandidate?.matchedGapPx ?? yCandidate?.matchedGapPx;

  return {
    axis,
    type: resolveCombinedSnapType(xCandidate, yCandidate),
    targetBlockId: primaryGuide?.targetBlockId,
    movingAnchor: primaryGuide?.movingAnchor,
    targetAnchor: primaryGuide?.targetAnchor,
    distancePx,
    priority: yCandidate?.priority ?? xCandidate?.priority,
    matchedGapPx,
  };
}

export function resolveMagneticAlignmentSnap({
  rawPreviewBox,
  activeXGuide,
  activeYGuide,
  snapDisabled,
  verticalAlignmentPriority,
}: {
  rawPreviewBox: AlignmentBox;
  activeXGuide?: ActiveAlignmentGuide;
  activeYGuide?: ActiveAlignmentGuide;
  snapDisabled: boolean;
  verticalAlignmentPriority?: string;
}): {
  magnetisedPreviewBox: AlignmentBox;
  appliedDelta: { x: number; y: number };
  debug: MagneticSnapDebug;
} {
  return resolveFineMoveMagneticSnap({
    rawPreviewBox,
    activeXGuide,
    activeYGuide,
    referenceBoxes: [],
    sectionWidth: 0,
    snapDisabled,
    verticalAlignmentPriority,
    includeEqualSpacing: false,
  });
}

export function resolveFineMoveMagneticSnap({
  rawPreviewBox,
  activeXGuide,
  activeYGuide,
  referenceBoxes,
  sectionWidth,
  movingBlockId,
  snapDisabled,
  verticalAlignmentPriority,
  includeEqualSpacing = true,
  rowPeerIds,
}: {
  rawPreviewBox: AlignmentBox;
  activeXGuide?: ActiveAlignmentGuide;
  activeYGuide?: ActiveAlignmentGuide;
  referenceBoxes: AlignmentBox[];
  sectionWidth: number;
  movingBlockId?: string;
  snapDisabled: boolean;
  verticalAlignmentPriority?: string;
  includeEqualSpacing?: boolean;
  rowPeerIds?: string[];
}): {
  magnetisedPreviewBox: AlignmentBox;
  appliedDelta: { x: number; y: number };
  debug: MagneticSnapDebug;
} {
  const rawSerialized = serializeAlignmentBox(rawPreviewBox);

  if (snapDisabled) {
    return {
      magnetisedPreviewBox: rawPreviewBox,
      appliedDelta: { x: 0, y: 0 },
      debug: {
        enabled: true,
        snapApplied: false,
        disabledReason: 'alt-key',
        rawPreviewBox: rawSerialized,
        magnetisedPreviewBox: rawSerialized,
        appliedDelta: { x: 0, y: 0 },
        thresholdPx: MAGNETIC_ALIGNMENT_THRESHOLD_PX,
        equalSpacingThresholdPx: MAGNETIC_EQUAL_SPACING_THRESHOLD_PX,
        rhythmGapThresholdPx: MAGNETIC_RHYTHM_GAP_THRESHOLD_PX,
        altKey: true,
      },
    };
  }

  const xCandidates: AxisMagneticCandidate[] = [];
  const yCandidates: AxisMagneticCandidate[] = [];
  let rhythmGaps: number[] | undefined;

  const xAxisAlign = resolveAxisMagneticDelta(activeXGuide, MAGNETIC_ALIGNMENT_THRESHOLD_PX);
  const yAxisAlign = resolveAxisMagneticDelta(activeYGuide, MAGNETIC_ALIGNMENT_THRESHOLD_PX);

  if (xAxisAlign) {
    xCandidates.push({
      delta: xAxisAlign.delta,
      distancePx: xAxisAlign.distancePx,
      type: 'alignment',
      guide: activeXGuide,
    });
  }
  if (yAxisAlign) {
    yCandidates.push({
      delta: yAxisAlign.delta,
      distancePx: yAxisAlign.distancePx,
      type: 'alignment',
      guide: activeYGuide,
      priority: verticalAlignmentPriority,
    });
  }

  if (includeEqualSpacing && referenceBoxes.length > 0) {
    const sectionHeight = Math.max(
      rawPreviewBox.bottom,
      ...referenceBoxes.map((box) => box.bottom),
      1
    );
    const peerIdSet = new Set(rowPeerIds ?? []);
    const reachableReferences = referenceBoxes.filter(
      (box) =>
        box.id !== movingBlockId &&
        (isAlignmentNeighborWithinReach(rawPreviewBox, box, 'x', sectionHeight, peerIdSet) ||
          isAlignmentNeighborWithinReach(rawPreviewBox, box, 'y', sectionWidth, peerIdSet))
    );

    // Gap guides resolve neighbours from the full reference set; match that here so
    // blue equal-spacing pills and magnetic pull stay in sync.
    const equalTargets = computeEqualSpacingSnapTargets(
      rawPreviewBox,
      referenceBoxes,
      sectionWidth,
      movingBlockId
    );

    if (equalTargets.distanceX <= MAGNETIC_EQUAL_SPACING_THRESHOLD_PX) {
      xCandidates.push({
        delta: clampMagneticAxisDelta(
          equalTargets.deltaX,
          equalTargets.distanceX,
          MAGNETIC_EQUAL_SPACING_THRESHOLD_PX,
          'spacing'
        ),
        distancePx: equalTargets.distanceX,
        type: 'equal-spacing-x',
      });
    }
    if (equalTargets.distanceY <= MAGNETIC_EQUAL_SPACING_Y_THRESHOLD_PX) {
      yCandidates.push({
        delta: clampMagneticAxisDelta(
          equalTargets.deltaY,
          equalTargets.distanceY,
          MAGNETIC_EQUAL_SPACING_Y_THRESHOLD_PX,
          'spacing'
        ),
        distancePx: equalTargets.distanceY,
        type: 'equal-spacing-y',
      });
    }

    const rhythmTargets = computeRhythmGapSnapTargets(
      rawPreviewBox,
      reachableReferences,
      sectionWidth,
      movingBlockId
    );
    rhythmGaps = rhythmTargets.rhythmGaps;

    if (rhythmTargets.distanceX <= MAGNETIC_RHYTHM_GAP_THRESHOLD_PX) {
      xCandidates.push({
        delta: clampMagneticAxisDelta(
          rhythmTargets.deltaX,
          rhythmTargets.distanceX,
          MAGNETIC_RHYTHM_GAP_THRESHOLD_PX,
          'spacing'
        ),
        distancePx: rhythmTargets.distanceX,
        type: 'matching-gap-x',
        matchedGapPx: rhythmTargets.matchedGapX,
      });
    }
    if (rhythmTargets.distanceY <= MAGNETIC_RHYTHM_GAP_THRESHOLD_PX) {
      yCandidates.push({
        delta: clampMagneticAxisDelta(
          rhythmTargets.deltaY,
          rhythmTargets.distanceY,
          MAGNETIC_RHYTHM_GAP_THRESHOLD_PX,
          'spacing'
        ),
        distancePx: rhythmTargets.distanceY,
        type: 'matching-gap-y',
        matchedGapPx: rhythmTargets.matchedGapY,
      });
    }
  }

  const bestX = pickAxisMagneticCandidate(xCandidates);
  const bestY = pickAxisMagneticCandidate(yCandidates);

  if (
    bestX?.type === 'matching-gap-x' &&
    bestX.matchedGapPx != null &&
    bestX.matchedGapPx > 0 &&
    referenceBoxes.length > 0 &&
  !yCandidates.some((candidate) => candidate.type === 'matching-gap-y')
  ) {
    const harmonicGap = bestX.matchedGapPx;
    const { topNeighbor, bottomNeighbor } = resolveGapNeighborContext(
      rawPreviewBox,
      referenceBoxes,
      sectionWidth,
      movingBlockId
    );
    if (topNeighbor) {
      const delta = topNeighbor.bottom + harmonicGap - rawPreviewBox.top;
      const distance = Math.abs(delta);
      if (distance <= MAGNETIC_RHYTHM_GAP_THRESHOLD_PX) {
        yCandidates.push({
          delta: clampMagneticAxisDelta(delta, distance, MAGNETIC_RHYTHM_GAP_THRESHOLD_PX, 'spacing'),
          distancePx: distance,
          type: 'matching-gap-y',
          matchedGapPx: harmonicGap,
        });
      }
    }
    if (bottomNeighbor) {
      const delta = bottomNeighbor.top - harmonicGap - rawPreviewBox.height - rawPreviewBox.top;
      const distance = Math.abs(delta);
      if (distance <= MAGNETIC_RHYTHM_GAP_THRESHOLD_PX) {
        yCandidates.push({
          delta: clampMagneticAxisDelta(delta, distance, MAGNETIC_RHYTHM_GAP_THRESHOLD_PX, 'spacing'),
          distancePx: distance,
          type: 'matching-gap-y',
          matchedGapPx: harmonicGap,
        });
      }
    }
  }

  const resolvedBestX = pickAxisMagneticCandidate(xCandidates);
  const resolvedBestY = pickAxisMagneticCandidate(yCandidates);

  if (!resolvedBestX && !resolvedBestY) {
    const hasGuide = Boolean(activeXGuide || activeYGuide);
    return {
      magnetisedPreviewBox: rawPreviewBox,
      appliedDelta: { x: 0, y: 0 },
      debug: {
        enabled: true,
        snapApplied: false,
        disabledReason: hasGuide ? 'outside-threshold' : 'no-active-guide',
        rawPreviewBox: rawSerialized,
        magnetisedPreviewBox: rawSerialized,
        appliedDelta: { x: 0, y: 0 },
        rhythmGaps,
        thresholdPx: MAGNETIC_ALIGNMENT_THRESHOLD_PX,
        equalSpacingThresholdPx: MAGNETIC_EQUAL_SPACING_THRESHOLD_PX,
        rhythmGapThresholdPx: MAGNETIC_RHYTHM_GAP_THRESHOLD_PX,
        altKey: false,
      },
    };
  }

  const appliedDelta = { x: resolvedBestX?.delta ?? 0, y: resolvedBestY?.delta ?? 0 };
  const magnetisedPreviewBox = translateAlignmentBox(
    rawPreviewBox,
    appliedDelta.x,
    appliedDelta.y
  );

  return {
    magnetisedPreviewBox,
    appliedDelta,
    debug: {
      enabled: true,
      snapApplied: appliedDelta.x !== 0 || appliedDelta.y !== 0,
      rawPreviewBox: rawSerialized,
      magnetisedPreviewBox: serializeAlignmentBox(magnetisedPreviewBox),
      appliedDelta,
      target: buildMagneticTarget(resolvedBestX, resolvedBestY),
      rhythmGaps,
      thresholdPx: MAGNETIC_ALIGNMENT_THRESHOLD_PX,
      equalSpacingThresholdPx: MAGNETIC_EQUAL_SPACING_THRESHOLD_PX,
      rhythmGapThresholdPx: MAGNETIC_RHYTHM_GAP_THRESHOLD_PX,
      altKey: false,
    },
  };
}
